import { APP_ICONS } from "@workspace/assets"
import type { AccessRequestProduct } from "@workspace/shared"
import { PRACTICE_APPS } from "@workspace/ui/lib/practiceApps"
import { APP_ID_BY_SESSIONS, AppId } from "../../constants/appRegistry"
import type { SessionValue, SessionsMap } from "./types"

// The dashboard's product picker (client 2026-11-04), ported from the
// in-game dashboard (platform-2.0 dashboard/components/dashboard/tiles.ts):
// the sidebar's app icons, whatever the subscriptions.

export type DashboardAppId = Exclude<AppId, AppId.Dashboard>

export type DashboardTileDef = {
  appId: DashboardAppId
  /** What the tile says under the icon: the app's full name, as the sidebar says it. */
  title: string
  icon: string
  /** The app's name for sessions and access requests ("LB"). */
  product: AccessRequestProduct
}

/**
 * The client's mockup order — Special Teams, Practice, Quarterback, Wide
 * Receiver, Running Back; Tight End, Offensive Line, Linebacker, Defensive
 * Back, Defensive Line; Safety — then the Defensive Coordinator's reports.
 */
const ORDER: readonly DashboardAppId[] = [
  AppId.ST,
  AppId.DEF,
  AppId.QB,
  AppId.WR,
  AppId.RB,
  AppId.TE,
  AppId.OL,
  AppId.LB,
  AppId.DB,
  AppId.DL,
  AppId.S,
  AppId.DC,
]

const nameOf = (appId: DashboardAppId) => PRACTICE_APPS.find((app) => app.id === appId)?.name ?? appId

export const DASHBOARD_TILES: readonly DashboardTileDef[] = ORDER.map((appId) => ({
  appId,
  title: nameOf(appId),
  icon: APP_ICONS[appId] ?? "",
  product: APP_ID_BY_SESSIONS[appId] as AccessRequestProduct,
}))

/**
 * - available: opens the app.
 * - unsubscribed: greyed out; tapping offers "Request Access" (an email to
 *   sales).
 * - locked: every one of the team's seats in the app is taken by other
 *   devices; a lock sits in the corner and the tile lists who is on.
 * - offline: offline, and the app has no data on this device to open into.
 */
export type TileState = "available" | "unsubscribed" | "locked" | "offline"

export function tileState(input: {
  subscribed: boolean
  online: boolean
  /** Opened online on this device before, so it has its data offline. */
  offlineReady: boolean
  session?: SessionsMap[string]
}): TileState {
  if (!input.subscribed) return "unsubscribed"
  if (!input.online && !input.offlineReady) return "offline"
  const session = input.session
  if (session?.sessionsAvailable === 0 && !session.values.some((value) => value.isCurrentDevice)) return "locked"
  return "available"
}

/** What a tap on a tile does. */
export type TileTapAction =
  | { kind: "open" }
  /** The Request Access dialog. */
  | { kind: "request" }
  /** The "all sessions in use" dialog. */
  | { kind: "seats" }
  /** Nothing opens; one line under the heading says why. */
  | { kind: "notice"; text: string }

export const OFFLINE_NOTICE = "Please connect to the internet to open this application."

export function tileTapAction(input: { state: TileState; online: boolean }): TileTapAction {
  switch (input.state) {
    case "available":
      return { kind: "open" }
    case "unsubscribed":
      // The request is an email: offline it could only fail.
      if (!input.online) return { kind: "notice", text: "Please connect to the internet to request access." }
      return { kind: "request" }
    case "locked":
      return { kind: "seats" }
    case "offline":
      return { kind: "notice", text: OFFLINE_NOTICE }
  }
}

/** An older API sends only the user's own sessions, without `isMine`. */
export const isMine = (value: SessionValue): boolean => value.isMine ?? true

export type SessionHolder = {
  /** Who: their email, or "You" when the API sent none for the user. */
  label: string
  /** How many of the app's seats they hold. */
  count: number
  mine: boolean
}

/**
 * Who holds an app's seats, one line each: "coach@school.org (3)" in the
 * client's mockup — the count only when it is more than one. Most seats
 * first, then by email.
 */
export function sessionHolders(values: readonly SessionValue[]): SessionHolder[] {
  const byHolder = new Map<string, SessionHolder>()
  for (const value of values) {
    const mine = isMine(value)
    const label = value.email?.trim() || (mine ? "You" : "A teammate")
    const holder = byHolder.get(label) ?? { label, count: 0, mine }
    holder.count += 1
    byHolder.set(label, holder)
  }
  return [...byHolder.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
}

export const holderLine = (holder: SessionHolder): string =>
  holder.count > 1 ? `${holder.label} (${holder.count})` : holder.label

/** The sessions map key for a dashboard app ("LB"). */
export const sessionKey = (appId: DashboardAppId): string => APP_ID_BY_SESSIONS[appId]

/**
 * Where an email may wrap in a column as narrow as a tile: after the "@" and
 * before a dot, never mid-word. "a@b.com" → ["a@", "b", ".com"].
 *
 * No regex lookbehind: older iPad Safari cannot even parse one, which would
 * take the whole dashboard down.
 */
export function emailParts(text: string): string[] {
  const parts: string[] = []
  let current = ""
  for (const ch of text) {
    if (ch === "." && current) {
      parts.push(current)
      current = ""
    }
    current += ch
    if (ch === "@") {
      parts.push(current)
      current = ""
    }
  }
  if (current) parts.push(current)
  return parts
}
